import { useEffect, useId } from "react";
import { SITE_URL } from "@/lib/supabase";
import { useI18n, type Lang } from "@/contexts/I18nContext";

const OG_LOCALE: Record<Lang, string> = {
  it: "it_CH",
  de: "de_CH",
  fr: "fr_CH",
  en: "en_CH",
};

interface SEOHeadProps {
  title: string;
  description: string;
  path?: string;
  image?: string;
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
  noIndex?: boolean;
}

const HREFLANGS: Array<{ code: string; prefix: string }> = [
  { code: "it", prefix: "" },
  { code: "de", prefix: "/de" },
  { code: "fr", prefix: "/fr" },
  { code: "en", prefix: "/en" },
];

// ---------------------------------------------------------------------------
// Schema.org helpers — import these in page components
// ---------------------------------------------------------------------------

export interface FaqItem { question: string; answer: string }

export function buildFaqSchema(items: FaqItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": items.map(({ question, answer }) => ({
      "@type": "Question",
      "name": question,
      "acceptedAnswer": { "@type": "Answer", "text": answer },
    })),
  }
}

export interface BreadcrumbItem { name: string; path: string }

export function buildBreadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map(({ name, path }, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": name,
      "item": `${SITE_URL}${path}`,
    })),
  }
}

// LocalBusiness / ProfessionalService — reusable across pages (priceRange is approximate)
// NOTE FOUNDER: streetAddress + telephone sono PLACEHOLDER. Sostituire con valori reali
// quando la sede legale è registrata e il numero di telefono aziendale è attivo.
export function buildLocalBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Swissez Sàrl",
    "alternateName": "FiscoTicino",
    "brand": { "@type": "Brand", "name": "FiscoTicino" },
    "description": "Fiduciario digitale per aziende e indipendenti del Canton Ticino — OCR documenti, chat fiduciario, contabilità doppia partita, QR-Bill svizzera, conformità nLPD/GeBüV.",
    "url": SITE_URL,
    "logo": `${SITE_URL}/logo-knot-lg.png`,
    "image": `${SITE_URL}/og-image.png`,
    "email": "info@swissez.ch",
    // PLACEHOLDER: numero di telefono aziendale (founder must replace with real)
    "telephone": "+41 91 XXX XX XX",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "CH",
      "addressRegion": "TI",
      "addressLocality": "Lugano",
      "postalCode": "6900",
      // PLACEHOLDER: indirizzo sede legale (founder must replace with real)
      "streetAddress": "Via Esempio 1",
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 46.0037,
      "longitude": 8.9511,
    },
    "areaServed": [
      { "@type": "AdministrativeArea", "name": "Ticino" },
      { "@type": "Country", "name": "Switzerland" },
    ],
    "priceRange": "CHF",
    "openingHours": "Mo-Fr 09:00-18:00",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00",
      },
    ],
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "contactType": "customer support",
        "email": "info@swissez.ch",
        "availableLanguage": ["it", "de", "fr", "en"],
        "areaServed": "CH",
      },
    ],
    "sameAs": [
      "https://swissez.ch",
    ],
  }
}

export interface PricingPlan {
  name: string
  price: string // numeric string e.g. "69" or "CHF 69"
  currency?: string
  description?: string
}

export function buildPricingProductSchema(plans: PricingPlan[]) {
  return plans.map((plan) => {
    const priceNumber = plan.price.replace(/[^0-9.]/g, "") || "0"
    const currency = plan.currency || "CHF"
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": `Swissez ${plan.name}`,
      "description": plan.description || `Piano ${plan.name} di Swissez`,
      "brand": { "@type": "Brand", "name": "Swissez" },
      "offers": {
        "@type": "Offer",
        "url": `${SITE_URL}/prezzi`,
        "priceCurrency": currency,
        "price": priceNumber,
        "availability": "https://schema.org/InStock",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "price": priceNumber,
          "priceCurrency": currency,
          "billingDuration": "P1M",
          "unitCode": "MON",
        },
      },
    }
  })
}

const DEFAULT_OG = "/og-image.png";

export default function SEOHead({ title, description, path = "", image, structuredData, noIndex = false }: SEOHeadProps) {
  const { t, lang } = useI18n();
  // useId() generates a stable, unique-per-instance id (React 18+).
  // Used to scope JSON-LD <script> ownership so concurrent mount/unmount
  // during route transitions cannot remove a sibling instance's scripts.
  const instanceId = useId();
  const defaultTitle = t("site.seo.default_title") || "Swissez — Fiscalità Semplificata";
  const ogImageAlt = t("site.seo.og_image_alt") || `${title} — Swissez`;

  useEffect(() => {
    return () => {
      document.title = defaultTitle;
    };
  }, [defaultTitle]);

  useEffect(() => {
    document.title = title;
    const url = `${SITE_URL}${path}`;
    const ogImage = `${SITE_URL}${image ?? DEFAULT_OG}`;

    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    // Remove any previous hreflang links managed by this component
    document.querySelectorAll('link[data-seohead-hreflang="true"]').forEach(l => l.remove());
    // S6: also clear hreflang/JSON-LD added at build-time by vite-prerender-plugin.
    // After SPA hydration, SEOHead is the source of truth; static prerender tags
    // would otherwise duplicate (Googlebot deduplicates, but cleaner DOM).
    document.querySelectorAll('link[data-prerender-hreflang="true"]').forEach(l => l.remove());
    document.querySelectorAll('script[data-prerender-jsonld="true"]').forEach(s => s.remove());

    // RSS alternate (static, single per page).
    let rssEl = document.querySelector('link[rel="alternate"][type="application/rss+xml"]') as HTMLLinkElement | null;
    if (!rssEl) {
      rssEl = document.createElement("link");
      rssEl.setAttribute("rel", "alternate");
      rssEl.setAttribute("type", "application/rss+xml");
      rssEl.setAttribute("title", "Swissez Blog");
      rssEl.setAttribute("href", "/blog/rss.xml");
      document.head.appendChild(rssEl);
    }

    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:image:alt", ogImageAlt);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:locale", OG_LOCALE[lang] ?? "it_CH");
    setMeta("property", "og:site_name", "Swissez");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage);
    setMeta("name", "twitter:image:alt", ogImageAlt);
    setLink("canonical", url);

    // Robots: set noindex when noIndex is true, clear otherwise
    setMeta("name", "robots", noIndex ? "noindex,nofollow" : "index,follow");

    // hreflang alternates: only when we have a concrete path and we index the page
    if (path && !noIndex) {
      for (const { code, prefix } of HREFLANGS) {
        const el = document.createElement("link");
        el.setAttribute("rel", "alternate");
        el.setAttribute("hreflang", code);
        el.setAttribute("href", `${SITE_URL}${prefix}${path}`);
        el.setAttribute("data-seohead-hreflang", "true");
        document.head.appendChild(el);
      }
      const xDefault = document.createElement("link");
      xDefault.setAttribute("rel", "alternate");
      xDefault.setAttribute("hreflang", "x-default");
      xDefault.setAttribute("href", `${SITE_URL}${path}`);
      xDefault.setAttribute("data-seohead-hreflang", "true");
      document.head.appendChild(xDefault);
    }

    // Remove only THIS instance's previous JSON-LD scripts (scoped by instanceId).
    // Using a global querySelector('[data-seohead="true"]') would race with a
    // concurrent SEOHead mount during route transitions, wiping the incoming
    // instance's scripts before it finishes mounting.
    document.querySelectorAll(`script[data-seohead-id="${instanceId}"]`).forEach(s => s.remove());
    if (structuredData) {
      const items = Array.isArray(structuredData) ? structuredData : [structuredData];
      for (const item of items) {
        const ldScript = document.createElement("script");
        ldScript.setAttribute("type", "application/ld+json");
        // data-seohead kept for backward compat (external tooling may query it).
        ldScript.setAttribute("data-seohead", "true");
        // data-seohead-id scopes ownership to this component instance.
        ldScript.setAttribute("data-seohead-id", instanceId);
        ldScript.textContent = JSON.stringify(item);
        document.head.appendChild(ldScript);
      }
    }

    return () => {
      // Cleanup: remove only scripts owned by this instance, never touching siblings.
      document.querySelectorAll(`script[data-seohead-id="${instanceId}"]`).forEach(s => s.remove());
      document.querySelectorAll('link[data-seohead-hreflang="true"]').forEach(l => l.remove());
    };
  }, [title, description, path, image, structuredData, noIndex, lang, ogImageAlt, instanceId]);

  return null;
}
